/**
 * Lešení Český Ráj — Hero2 canvas image-sequence
 * Depends on: gsap, ScrollTrigger (loaded from the Hero2 section)
 *
 * Hybrid: initial scrub → cinematic autoplay from TRIGGER_FRAME
 * (frames play on a clock; pin stays until phase 3) → unpin and scroll
 * to Konfigurátor while remaining frames keep playing → restore pin
 * for permanent full scrub. Autoplay runs once per page load.
 */
(function () {
  'use strict';

  var TRIGGER_ID = 'leseni-hero2';
  var MOBILE_QUERY = '(max-width: 768px)';
  /** If mobile frames would letterbox more than this many px of width, use desktop frames. */
  var MOBILE_WIDTH_ZOOM_MAX_PX = 60;
  /** Higher = snappier follow, lower = more lag. ~6 ≈ 160ms time constant. */
  var LERP_SPEED = 6;
  var SNAP_EPSILON = 0.0005;

  // Fallbacks if section settings are missing. Prefer theme editor.
  const TRIGGER_FRAME = 10;
  const SLOWDOWN_FRAME = 170;
  const PHASE1_DURATION = 2.2;
  const PHASE2_DURATION = 1.6;
  const PHASE3_DURATION = 1.2;
  const OVERLAP_TIME = 0.8;

  var rafId = 0;
  var lastTs = 0;
  var targetProgress = 0;
  var displayedProgress = 0;
  var drawnIndex = -1;
  var drawnFadeKey = '';
  var lastUiProgress = -1;
  var frames = [];
  var frameUrls = [];
  var loadGen = 0;
  var canvas = null;
  var ctx = null;
  var mqBound = false;
  var activeIsMobile = null;
  var activeFrameSet = null;
  var mobileFrameSize = { w: 0, h: 0 };
  var mediaCssW = 0;
  var mediaCssH = 0;
  var lastMediaBoxKey = '';
  var mediaEl = null;
  var progressFill = null;
  var progressKnobTravel = null;
  var resizeBound = false;
  var frameTexts = [];
  var lastTextFrame = -1;
  var fadeMax = 0;
  var fadeMaxMobile = 0;
  var gsapRef = null;
  var scrollTrigger = null;
  var fadeTween = null;
  var heroRoot = null;
  var pinEl = null;
  var screensCount = 4;
  var autoplayStartFrame = TRIGGER_FRAME;
  var slowdownFrame = SLOWDOWN_FRAME;
  var phase1DurationSec = PHASE1_DURATION;
  var phase2DurationSec = PHASE2_DURATION;
  var phase3DurationSec = PHASE3_DURATION;
  var overlapTimeSec = OVERLAP_TIME;
  var hasAutoPlayed = false;
  var introChecked = false;
  var autoPlaying = false;
  var autoplayTimeline = null;
  var blockersBound = false;
  var jumpInProgress = false;
  var playCueBound = false;
  var pinCollapsedForHandoff = false;
  var restoringPin = false;
  var handoffRestorePending = false;
  var snappingHandoff = false;
  var handoffShiftEls = null;
  var handoffSticky = [];
  var handoffFromY = 0;
  var handoffToY = 0;
  var handoffShiftActive = false;

  function waitForGsap(cb) {
    if (window.gsap && window.ScrollTrigger) {
      cb(window.gsap, window.ScrollTrigger);
      return;
    }
    var tries = 0;
    var id = setInterval(function () {
      tries += 1;
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(id);
        cb(window.gsap, window.ScrollTrigger);
      } else if (tries > 80) {
        clearInterval(id);
      }
    }, 50);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobileViewport() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function clamp01(v) {
    return Math.max(0, Math.min(1, v));
  }

  function clampDurationSec(value, fallback, maxSec) {
    var d = parseFloat(value);
    var max = isFinite(maxSec) ? maxSec : 5;
    if (!isFinite(d) || d <= 0) d = fallback;
    d = Math.round(d * 10) / 10;
    if (d < 0.1) d = 0.1;
    if (d > max) d = max;
    return d;
  }

  function clampOverlapSec(value, fallback) {
    var d = parseFloat(value);
    if (!isFinite(d) || d < 0) d = fallback;
    d = Math.round(d * 10) / 10;
    if (d > 10) d = 10;
    return d;
  }

  function getNextSectionEl() {
    if (heroRoot) {
      var section = heroRoot.closest('.shopify-section');
      var next = section && section.nextElementSibling;
      if (next) return next;
    }
    return document.querySelector('[id^="StarterConfig-"], .starter-config');
  }

  function getNextSectionAlignOffset(el) {
    if (!el) el = getNextSectionEl();
    if (!el) return 0;
    try {
      return Math.max(0, parseFloat(window.getComputedStyle(el).paddingTop) || 0);
    } catch (e) {
      return 0;
    }
  }

  function getNextSectionScrollY() {
    var el = getNextSectionEl();
    if (!el) return 0;
    var y = el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
    y += getNextSectionAlignOffset(el);
    return Math.max(0, Math.round(y));
  }

  function revealHero2Fx() {
    document.documentElement.classList.remove('is-hero2-fx-deferred');
  }

  function setHandoffActive(on) {
    document.documentElement.classList.toggle('is-hero2-handoff', !!on);
  }

  function collectHandoffShiftEls() {
    if (handoffShiftEls) return handoffShiftEls;
    handoffShiftEls = [];
    var header = document.querySelector('.header-wrapper.leseni-header');
    var main = document.getElementById('MainContent');
    var footer = document.querySelector('.shopify-section-group-footer-group');
    if (header) handoffShiftEls.push(header);
    if (main) handoffShiftEls.push(main);
    if (footer) handoffShiftEls.push(footer);
    return handoffShiftEls;
  }

  function stickyHandoffEl() {
    var mobile = window.matchMedia('(max-width: 989px)').matches;
    return document.querySelector(mobile ? '.starter-config__scene' : '.starter-config__aside');
  }

  function captureStickyHandoff() {
    handoffSticky = [];
    var el = stickyHandoffEl();
    if (!el) return;
    var cs = window.getComputedStyle(el);
    var pos = cs.position;
    if (pos !== 'sticky' && pos !== '-webkit-sticky') return;
    var rect = el.getBoundingClientRect();
    var parent = el.parentElement;
    var parentRect = parent ? parent.getBoundingClientRect() : rect;
    var stickAt = parseFloat(cs.top);
    if (!isFinite(stickAt)) stickAt = 0;
    handoffSticky.push({
      el: el,
      startTop: rect.top,
      stickAt: stickAt,
      maxExtra: Math.max(0, parentRect.bottom - rect.bottom)
    });
  }

  function applyStickyHandoff(delta) {
    var i;
    var item;
    var extra;
    var t;
    for (i = 0; i < handoffSticky.length; i += 1) {
      item = handoffSticky[i];
      extra = delta - (item.startTop - item.stickAt);
      if (extra < 0) extra = 0;
      if (extra > item.maxExtra) extra = item.maxExtra;
      extra = Math.round(extra);
      t = extra ? 'translate3d(0,' + extra + 'px,0)' : '';
      item.el.style.transform = t;
      item.el.style.webkitTransform = t;
    }
  }

  function clearStickyHandoff() {
    var i;
    for (i = 0; i < handoffSticky.length; i += 1) {
      handoffSticky[i].el.style.transform = '';
      handoffSticky[i].el.style.webkitTransform = '';
      handoffSticky[i].el.style.willChange = '';
    }
    handoffSticky = [];
  }

  function applyHandoffShift(y) {
    var els = collectHandoffShiftEls();
    var t = y ? 'translate3d(0,' + -Math.round(y) + 'px,0)' : '';
    var i;
    for (i = 0; i < els.length; i += 1) {
      if (t) {
        els[i].style.transform = t;
        els[i].style.webkitTransform = t;
      } else {
        els[i].style.transform = '';
        els[i].style.webkitTransform = '';
        els[i].style.willChange = '';
      }
    }
    applyStickyHandoff(y);
  }

  function prepareHandoffShift() {
    handoffShiftActive = true;
    setHandoffActive(true);
    var els = collectHandoffShiftEls();
    var i;
    for (i = 0; i < els.length; i += 1) {
      els[i].style.willChange = 'transform';
    }
    for (i = 0; i < handoffSticky.length; i += 1) {
      handoffSticky[i].el.style.willChange = 'transform';
    }
  }

  function clearHandoffShift() {
    applyHandoffShift(0);
    clearStickyHandoff();
    setHandoffActive(false);
    handoffShiftEls = null;
    handoffShiftActive = false;
  }

  function commitHandoffShift() {
    if (!handoffShiftActive) return;
    var y = isFinite(handoffToY) ? handoffToY : getNextSectionScrollY();
    var html = document.documentElement;
    var prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, Math.max(0, Math.round(y || 0)));
    clearHandoffShift();
    html.style.scrollBehavior = prevBehavior;
  }

  function snapToHandoffLanding() {
    if (snappingHandoff) return;
    snappingHandoff = true;
    window.scrollTo(0, getNextSectionScrollY());
    if (window.ScrollTrigger && typeof window.ScrollTrigger.update === 'function') {
      window.ScrollTrigger.update();
    }
    snappingHandoff = false;
  }

  function lerp(start, end, t) {
    return start + (end - start) * t;
  }

  function parseFrameJson(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return [];
    try {
      var data = JSON.parse(el.textContent);
      return Array.isArray(data) ? data.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  function viewportBox() {
    var pw = pinEl && pinEl.clientWidth ? pinEl.clientWidth : window.innerWidth;
    var ph = pinEl && pinEl.clientHeight ? pinEl.clientHeight : window.innerHeight;
    return { w: Math.max(1, pw), h: Math.max(1, ph) };
  }

  function containedMissingWidth(iw, ih, pw, ph) {
    if (!iw || !ih || !pw || !ph) return 0;
    var scale = Math.min(pw / iw, ph / ih);
    return Math.max(0, pw - iw * scale);
  }

  function rememberMobileFrameSize(image) {
    if (!image) return;
    var w = image.naturalWidth || image.width;
    var h = image.naturalHeight || image.height;
    if (w && h) {
      mobileFrameSize.w = w;
      mobileFrameSize.h = h;
    }
  }

  function computeFrameSet() {
    if (!isMobileViewport()) return 'desktop';
    var box = viewportBox();
    if (!mobileFrameSize.w || !mobileFrameSize.h) return 'mobile';
    var missing = containedMissingWidth(mobileFrameSize.w, mobileFrameSize.h, box.w, box.h);
    return missing > MOBILE_WIDTH_ZOOM_MAX_PX ? 'desktop' : 'mobile';
  }

  function urlsForFrameSet(root, frameSet) {
    if (frameSet === 'mobile') {
      var mobileUrls = parseFrameJson(root, '[data-leseni-hero2-frames-mobile]');
      if (mobileUrls.length) return mobileUrls;
    }
    return parseFrameJson(root, '[data-leseni-hero2-frames-desktop]');
  }

  function cacheUiNodes(root) {
    mediaEl = root ? root.querySelector('.leseni-hero2__media') : null;
    progressFill = root ? root.querySelector('[data-leseni-hero2-progress-fill]') : null;
    progressKnobTravel = root ? root.querySelector('[data-leseni-hero2-progress-knob-travel]') : null;
  }

  function setProgressUI(root, progress) {
    var p = clamp01(progress);
    if (Math.abs(p - lastUiProgress) < 0.002) return;
    lastUiProgress = p;
    if (!progressFill && root) {
      progressFill = root.querySelector('[data-leseni-hero2-progress-fill]');
    }
    if (!progressKnobTravel && root) {
      progressKnobTravel = root.querySelector('[data-leseni-hero2-progress-knob-travel]');
    }
    if (progressFill) progressFill.style.transform = 'translateZ(0) scaleY(' + p + ')';
    if (progressKnobTravel) {
      progressKnobTravel.style.transform = 'translate3d(0,' + (p * 100) + '%,0)';
    }
  }

  function fadeScrollHint() {
    var root = heroRoot || document.querySelector('[data-leseni-hero2]');
    if (!root) return;
    var hint = root.querySelector('[data-leseni-hero2-scroll-hint]');
    if (!hint) return;
    hint.classList.add('is-faded');
    if (hint.tagName === 'BUTTON') {
      hint.setAttribute('disabled', 'disabled');
      hint.setAttribute('aria-hidden', 'true');
      if (document.activeElement === hint) hint.blur();
    }
  }

  function resetScrollHint(root) {
    var hintRoot = root || document.querySelector('[data-leseni-hero2]');
    if (!hintRoot) return;
    var hint = hintRoot.querySelector('[data-leseni-hero2-scroll-hint]');
    if (!hint) return;
    hint.classList.remove('is-faded');
    hint.removeAttribute('disabled');
    hint.removeAttribute('aria-hidden');
  }

  function jumpToAutoplay() {
    if (hasAutoPlayed || autoPlaying || jumpInProgress) return;
    if (prefersReducedMotion()) return;
    if (!scrollTrigger || !heroRoot) return;

    jumpInProgress = true;

    var p = frameToProgress(autoplayStartFrame);
    displayedProgress = p;
    applySequenceProgress(p);
    var idx = progressToIndex();
    drawIndex(idx, true);
    updateFrameTexts(idx);
    lastUiProgress = -1;
    setProgressUI(heroRoot, p);

    jumpInProgress = false;
    startCinematicAutoplay();
  }

  function onPlayCueClick(event) {
    if (event) event.preventDefault();
    jumpToAutoplay();
  }

  function bindPlayCue() {
    if (playCueBound || !heroRoot) return;
    var cue = heroRoot.querySelector('[data-leseni-hero2-play-cue]');
    if (!cue) return;
    playCueBound = true;
    cue.addEventListener('click', onPlayCueClick);
  }

  function unbindPlayCue() {
    var root = heroRoot || document.querySelector('[data-leseni-hero2]');
    var cue = root && root.querySelector('[data-leseni-hero2-play-cue]');
    if (cue) cue.removeEventListener('click', onPlayCueClick);
    playCueBound = false;
  }

  function rememberPinSize() {
    if (!pinEl) {
      mediaCssW = Math.max(1, window.innerWidth);
      mediaCssH = Math.max(1, window.innerHeight);
      return;
    }
    mediaCssW = Math.max(1, pinEl.clientWidth);
    mediaCssH = Math.max(1, pinEl.clientHeight);
  }

  function resizeCanvas() {
    if (!canvas) return false;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var cssW = mediaCssW || (canvas.clientWidth || window.innerWidth);
    var cssH = mediaCssH || (canvas.clientHeight || window.innerHeight);
    var w = Math.max(1, Math.round(cssW * dpr));
    var h = Math.max(1, Math.round(cssH * dpr));
    if (canvas.width === w && canvas.height === h) return false;
    canvas.width = w;
    canvas.height = h;
    drawnIndex = -1;
    drawnFadeKey = '';
    return true;
  }

  function getMediaEl() {
    return mediaEl || (heroRoot && heroRoot.querySelector('.leseni-hero2__media'));
  }

  function resetMediaBox() {
    var media = getMediaEl();
    rememberPinSize();
    if (!media) return;
    if (lastMediaBoxKey === 'full') return;
    lastMediaBoxKey = 'full';
    media.style.removeProperty('top');
    media.style.removeProperty('left');
    media.style.removeProperty('width');
    media.style.removeProperty('height');
  }

  function layoutMediaBox(image) {
    var media = getMediaEl();
    if (!media || !pinEl) return;
    if (activeFrameSet !== 'mobile' || !image) {
      resetMediaBox();
      return;
    }
    var pw = pinEl.clientWidth;
    var ph = pinEl.clientHeight;
    var iw = image.naturalWidth || image.width;
    var ih = image.naturalHeight || image.height;
    if (!pw || !ph || !iw || !ih) return;
    var containScale = Math.min(pw / iw, ph / ih);
    var dw = iw * containScale;
    var dh = ih * containScale;
    var missingW = Math.max(0, pw - dw);
    if (missingW > 0 && missingW <= MOBILE_WIDTH_ZOOM_MAX_PX) {
      var zoom = pw / iw;
      dw = pw;
      dh = ih * zoom;
    }
    var top = (ph - dh) / 2;
    var left = (pw - dw) / 2;
    var key = 'm:' + pw + 'x' + ph + ':' + Math.round(dw) + 'x' + Math.round(dh);
    mediaCssW = dw;
    mediaCssH = dh;
    if (lastMediaBoxKey === key) return;
    lastMediaBoxKey = key;
    media.style.top = top + 'px';
    media.style.left = left + 'px';
    media.style.width = dw + 'px';
    media.style.height = dh + 'px';
  }

  function fadeAmounts() {
    var top = 0;
    var bottom = 0;
    if (activeFrameSet === 'mobile' || activeIsMobile) {
      top = fadeMaxMobile > 0 ? fadeMaxMobile / 100 : 0;
      bottom = top;
    } else if (fadeMax > 0) {
      bottom = clamp01(displayedProgress) * (fadeMax / 100);
    }
    return { top: top, bottom: bottom };
  }

  function applyCanvasEdgeFade(cw, ch, fadeTop, fadeBottom) {
    if (!ctx || !cw || !ch) return;
    if (fadeTop <= 0 && fadeBottom <= 0) return;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    if (fadeTop > 0) {
      var th = Math.max(1, Math.round(ch * Math.min(0.49, fadeTop)));
      var topGrad = ctx.createLinearGradient(0, 0, 0, th);
      topGrad.addColorStop(0, 'rgba(0,0,0,1)');
      topGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = topGrad;
      ctx.fillRect(0, 0, cw, th);
    }
    if (fadeBottom > 0) {
      var bh = Math.max(1, Math.round(ch * Math.min(0.49, fadeBottom)));
      var y0 = ch - bh;
      var botGrad = ctx.createLinearGradient(0, y0, 0, ch);
      botGrad.addColorStop(0, 'rgba(0,0,0,0)');
      botGrad.addColorStop(1, 'rgba(0,0,0,1)');
      ctx.fillStyle = botGrad;
      ctx.fillRect(0, y0, cw, bh);
    }
    ctx.restore();
  }

  function drawFrame(image) {
    if (!ctx || !canvas || !image) return;
    resizeCanvas();
    var cw = canvas.width;
    var ch = canvas.height;
    var iw = image.naturalWidth || image.width;
    var ih = image.naturalHeight || image.height;
    if (!cw || !ch || !iw || !ih) return;

    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, cw, ch);

    var fade = fadeAmounts();
    if (activeFrameSet === 'mobile') {
      ctx.drawImage(image, 0, 0, cw, ch);
    } else {
      var scale = Math.max(cw / iw, ch / ih);
      var dw = iw * scale;
      var dh = ih * scale;
      var dx = (cw - dw) / 2;
      ctx.drawImage(image, dx, 0, dw, dh);
    }
    applyCanvasEdgeFade(cw, ch, fade.top, fade.bottom);
    drawnFadeKey = fade.top.toFixed(4) + ':' + fade.bottom.toFixed(4);
  }

  function frameAt(index) {
    if (index < 0 || index >= frames.length) return null;
    var img = frames[index];
    if (img && img.complete && (img.naturalWidth || img.width)) return img;
    return null;
  }

  function nearestLoaded(index) {
    var img = frameAt(index);
    if (img) return { img: img, index: index };
    var i;
    for (i = index - 1; i >= 0; i -= 1) {
      img = frameAt(i);
      if (img) return { img: img, index: i };
    }
    for (i = index + 1; i < frames.length; i += 1) {
      img = frameAt(i);
      if (img) return { img: img, index: i };
    }
    return null;
  }

  function drawIndex(index, force) {
    var found = nearestLoaded(index);
    if (!found) return;
    if (!force && found.index === drawnIndex) return;
    drawFrame(found.img);
    drawnIndex = found.index;
  }

  function maxFrameIndex() {
    return Math.max(0, frames.length - 1);
  }

  function progressToIndex() {
    return Math.round(clamp01(displayedProgress) * maxFrameIndex());
  }

  function triggerFrameIndex() {
    var maxIndex = maxFrameIndex();
    var index = autoplayStartFrame - 1;
    if (index < 0) index = 0;
    if (index > maxIndex) index = maxIndex;
    return index;
  }

  function frameToProgress(frame1Based) {
    var maxIndex = maxFrameIndex();
    if (maxIndex < 1) return 1;
    var index = frame1Based - 1;
    if (index < 0) index = 0;
    if (index > maxIndex) index = maxIndex;
    return index / maxIndex;
  }

  function collectFrameTexts(root) {
    frameTexts = [];
    lastTextFrame = -1;
    var mobile = isMobileViewport();
    var nodes = root.querySelectorAll('[data-leseni-hero2-frame-text]');
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      var el = nodes[i];
      var isMobileEl = el.classList.contains('leseni-hero2__frame-text--mobile');
      if (mobile !== isMobileEl) {
        el.classList.remove('is-visible');
        continue;
      }
      el.classList.add('is-visible');
      frameTexts.push({ el: el });
    }
  }

  function updateFrameTexts() {
    var i;
    for (i = 0; i < frameTexts.length; i += 1) {
      frameTexts[i].el.classList.add('is-visible');
    }
  }

  function syncHeader(isPinned) {
    if (window.LeseniHero2Header && typeof window.LeseniHero2Header.sync === 'function') {
      window.LeseniHero2Header.sync(isPinned);
    }
  }

  function applySequenceProgress(progress) {
    targetProgress = clamp01(progress);
    if (autoPlaying) displayedProgress = targetProgress;
  }

  function setHeroPinnedState(active) {
    var section = heroRoot && heroRoot.closest('.shopify-section');
    if (section) section.classList.toggle('is-hero2-pinned', !!active);
  }

  function tick(ts) {
    rafId = requestAnimationFrame(tick);

    if (!lastTs) lastTs = ts;
    var dt = Math.min(0.05, (ts - lastTs) / 1000);
    lastTs = ts;

    if (autoPlaying) {
      displayedProgress = targetProgress;
    } else {
      var alpha = 1 - Math.exp(-LERP_SPEED * dt);
      displayedProgress = lerp(displayedProgress, targetProgress, alpha);
    }

    if (Math.abs(targetProgress - displayedProgress) < SNAP_EPSILON) {
      displayedProgress = targetProgress;
    }

    var currentFrame = progressToIndex();
    var fade = fadeAmounts();
    var fadeKey = fade.top.toFixed(4) + ':' + fade.bottom.toFixed(4);
    if (currentFrame !== drawnIndex || fadeKey !== drawnFadeKey) {
      drawIndex(currentFrame, fadeKey !== drawnFadeKey);
    }
    if (heroRoot) setProgressUI(heroRoot, displayedProgress);
  }

  function startRaf() {
    if (rafId) cancelAnimationFrame(rafId);
    lastTs = 0;
    rafId = requestAnimationFrame(tick);
  }

  function trimTrailingEmpty() {
    var i = frames.length - 1;
    while (i > 0 && !frameAt(i)) {
      i -= 1;
    }
    if (i + 1 < frames.length) {
      frames.length = i + 1;
      frameUrls.length = i + 1;
    }
  }

  function preloadRest(urls, gen) {
    var i;
    var pending = Math.max(0, urls.length - 1);
    var finishOne = function () {
      pending -= 1;
      if (pending <= 0 && gen === loadGen) trimTrailingEmpty();
    };

    for (i = 1; i < urls.length; i += 1) {
      (function (index, src) {
        var img = new Image();
        img.decoding = 'async';
        img.onload = function () {
          if (gen !== loadGen) return;
          frames[index] = img;
          if (index === progressToIndex() && index !== drawnIndex) {
            drawIndex(index);
          }
          finishOne();
        };
        img.onerror = function () {
          if (gen !== loadGen) return;
          finishOne();
        };
        img.src = src;
      })(i, urls[i]);
    }

    if (!pending && gen === loadGen) trimTrailingEmpty();
  }

  function loadSequence(root) {
    loadGen += 1;
    var gen = loadGen;
    var wantSet = computeFrameSet();
    frameUrls = urlsForFrameSet(root, wantSet);
    if (wantSet === 'mobile' && !frameUrls.length) {
      wantSet = 'desktop';
      frameUrls = urlsForFrameSet(root, 'desktop');
    }
    frames = new Array(frameUrls.length);
    drawnIndex = -1;
    drawnFadeKey = '';
    activeIsMobile = isMobileViewport();
    activeFrameSet = wantSet;

    if (!canvas || !frameUrls.length) return;

    rememberPinSize();
    resizeCanvas();

    var first = new Image();
    first.decoding = 'async';
    first.onload = function () {
      if (gen !== loadGen) return;
      if (wantSet === 'mobile') {
        rememberMobileFrameSize(first);
        if (computeFrameSet() === 'desktop') {
          loadSequence(root);
          return;
        }
      }
      frames[0] = first;
      layoutMediaBox(first);
      drawIndex(0, true);
      preloadRest(frameUrls, gen);
    };
    first.src = frameUrls[0];
  }

  function onAutoplayWheelTouch(event) {
    if (!autoPlaying) return;
    event.preventDefault();
  }

  function onAutoplayKey(event) {
    if (!autoPlaying) return;
    var key = event.key;
    if (
      key === 'ArrowDown' ||
      key === 'ArrowUp' ||
      key === 'PageDown' ||
      key === 'PageUp' ||
      key === ' ' ||
      key === 'Home' ||
      key === 'End'
    ) {
      event.preventDefault();
    }
  }

  function bindAutoplayBlockers() {
    if (blockersBound) return;
    blockersBound = true;
    window.addEventListener('wheel', onAutoplayWheelTouch, { passive: false });
    window.addEventListener('touchmove', onAutoplayWheelTouch, { passive: false });
    window.addEventListener('keydown', onAutoplayKey, { passive: false });
  }

  function unbindAutoplayBlockers() {
    if (!blockersBound) return;
    blockersBound = false;
    window.removeEventListener('wheel', onAutoplayWheelTouch);
    window.removeEventListener('touchmove', onAutoplayWheelTouch);
    window.removeEventListener('keydown', onAutoplayKey);
  }

  function measureScrollbarWidth() {
    if (isMobileViewport()) return 0;
    var w = window.innerWidth - document.documentElement.clientWidth;
    if (!isFinite(w) || w < 0 || w > 40) return 0;
    return Math.round(w);
  }

  function revertPinLayout() {
    if (!pinEl) return;
    pinEl.style.removeProperty('width');
    pinEl.style.removeProperty('max-width');
    pinEl.style.removeProperty('min-width');
    pinEl.style.removeProperty('height');
    pinEl.style.removeProperty('top');
    pinEl.style.removeProperty('left');
    pinEl.style.removeProperty('right');
    pinEl.style.removeProperty('bottom');
    pinEl.style.removeProperty('inset');
    pinEl.style.removeProperty('position');
    pinEl.style.removeProperty('margin');
    pinEl.style.removeProperty('padding');
    pinEl.style.removeProperty('transform');
    pinEl.style.removeProperty('box-sizing');
    pinEl.style.removeProperty('z-index');
  }

  function lockUserInput() {
    document.body.style.pointerEvents = 'none';
    document.documentElement.style.setProperty('--leseni-cinematic-sbw', measureScrollbarWidth() + 'px');
    document.documentElement.classList.add('is-cinematic-locked');
    document.body.classList.add('is-cinematic-locked');
    bindAutoplayBlockers();
  }

  function unlockUserInput() {
    document.body.style.pointerEvents = '';
    document.documentElement.classList.remove('is-cinematic-locked');
    document.body.classList.remove('is-cinematic-locked');
    document.documentElement.style.removeProperty('--leseni-cinematic-sbw');
    unbindAutoplayBlockers();
  }

  function killAutoplayTimeline() {
    if (autoplayTimeline && gsapRef) {
      autoplayTimeline.kill();
    }
    autoplayTimeline = null;
  }

  function snapScrollToSequenceEnd() {
    if (!scrollTrigger) return;
    var y = Math.max(0, Math.round(scrollTrigger.end - 1));
    window.scrollTo(0, y);
    if (window.ScrollTrigger && typeof window.ScrollTrigger.update === 'function') {
      window.ScrollTrigger.update();
    }
  }

  function parkScrollAtPinStart() {
    if (!scrollTrigger) return;
    var y = Math.max(0, Math.round(scrollTrigger.start));
    if (Math.abs((window.scrollY || window.pageYOffset || 0) - y) < 1) return;
    jumpInProgress = true;
    window.scrollTo(0, y);
    if (window.ScrollTrigger && typeof window.ScrollTrigger.update === 'function') {
      window.ScrollTrigger.update();
    }
    jumpInProgress = false;
  }

  function unpinHeroForHandoff() {
    if (pinCollapsedForHandoff || !heroRoot) return;
    pinCollapsedForHandoff = true;
    restoringPin = true;

    parkScrollAtPinStart();

    if (fadeTween) {
      if (fadeTween.scrollTrigger) fadeTween.scrollTrigger.kill();
      fadeTween.kill();
      fadeTween = null;
    }

    if (scrollTrigger) {
      scrollTrigger.kill();
      scrollTrigger = null;
    }
    setHeroPinnedState(false);
    revertPinLayout();

    var section = heroRoot.closest('.shopify-section') || heroRoot;
    var y = section.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
    window.scrollTo(0, Math.max(0, Math.round(y)));
    restoringPin = false;
  }

  function restorePinAfterHandoff() {
    if (!pinCollapsedForHandoff) return;
    if (!heroRoot || !pinEl || !window.ScrollTrigger) {
      pinCollapsedForHandoff = false;
      return;
    }

    restoringPin = true;
    pinCollapsedForHandoff = false;
    jumpInProgress = true;

    var pinH = pinEl.offsetHeight || window.innerHeight;
    var extra = Math.round(Math.max(screensCount, 1) * pinH - pinH);
    var dummy = null;
    if (extra > 0) {
      var heroSection = heroRoot.closest('.shopify-section') || heroRoot;
      dummy = document.createElement('div');
      dummy.setAttribute('aria-hidden', 'true');
      dummy.style.cssText = 'height:' + extra + 'px;margin:0;padding:0;border:0;pointer-events:none;';
      heroSection.parentNode.insertBefore(dummy, heroSection.nextSibling);
      window.scrollTo(0, Math.max(0, Math.round((window.scrollY || window.pageYOffset || 0) + extra)));
    }

    handoffRestorePending = true;
    createSequenceTrigger(window.ScrollTrigger);
    if (typeof window.ScrollTrigger.refresh === 'function') {
      window.ScrollTrigger.refresh();
    }

    if (dummy && dummy.parentNode) dummy.parentNode.removeChild(dummy);
    if (typeof window.ScrollTrigger.refresh === 'function') {
      window.ScrollTrigger.refresh();
    }

    snapToHandoffLanding();

    if (!autoPlaying) {
      applySequenceProgress(1);
      displayedProgress = 1;
      if (heroRoot) setProgressUI(heroRoot, 1);
    }

    handoffRestorePending = false;
    jumpInProgress = false;
    restoringPin = false;
    setHeroPinnedState(!!(scrollTrigger && scrollTrigger.isActive));
    syncHeader();
  }

  function finishCinematicAutoplay() {
    killAutoplayTimeline();
    hasAutoPlayed = true;
    applySequenceProgress(1);
    displayedProgress = 1;
    if (heroRoot) setProgressUI(heroRoot, 1);
    autoPlaying = false;
    commitHandoffShift();
    if (pinCollapsedForHandoff) restorePinAfterHandoff();
    unlockUserInput();
    revealHero2Fx();
    if (window.ScrollTrigger && typeof window.ScrollTrigger.update === 'function') {
      window.ScrollTrigger.update();
    }
    if (!restoringPin) syncHeader();
  }

  function startCinematicAutoplay() {
    fadeScrollHint();
    if (hasAutoPlayed || autoPlaying || !gsapRef || !scrollTrigger) return;
    if (prefersReducedMotion()) {
      hasAutoPlayed = true;
      revealHero2Fx();
      return;
    }

    autoPlaying = true;
    lockUserInput();
    parkScrollAtPinStart();

    var startP = clamp01(displayedProgress);
    var slowP = frameToProgress(slowdownFrame);
    if (slowP < startP) slowP = startP;
    var endP = 1;
    var d1 = Math.abs(slowP - startP) < SNAP_EPSILON ? 0 : phase1DurationSec;
    var d2 = Math.abs(endP - slowP) < SNAP_EPSILON ? 0 : phase2DurationSec;
    var overlap = overlapTimeSec;
    if (overlap > d2) overlap = d2;
    var handoffAt = d1 + d2 - overlap;
    if (handoffAt < 0) handoffAt = 0;
    var hasNext = !!getNextSectionEl();

    killAutoplayTimeline();
    var proxy = { p: startP };
    applySequenceProgress(startP);

    autoplayTimeline = gsapRef.timeline({
      onComplete: finishCinematicAutoplay
    });

    if (d1 > 0) {
      autoplayTimeline.to(
        proxy,
        {
          p: slowP,
          duration: d1,
          ease: 'none',
          onUpdate: function () {
            applySequenceProgress(proxy.p);
          }
        },
        0
      );
    }

    if (d2 > 0) {
      autoplayTimeline.to(
        proxy,
        {
          p: endP,
          duration: d2,
          ease: 'power2.out',
          onUpdate: function () {
            applySequenceProgress(proxy.p);
          }
        },
        d1
      );
    } else {
      applySequenceProgress(endP);
    }

    if (hasNext && phase3DurationSec > 0) {
      var unpinAt = handoffAt > 0.28 ? handoffAt - 0.18 : handoffAt;
      autoplayTimeline.call(
        function () {
          unpinHeroForHandoff();
        },
        null,
        unpinAt
      );

      var scrollProxy = { t: 0 };
      autoplayTimeline.to(
        scrollProxy,
        {
          t: 1,
          duration: phase3DurationSec,
          ease: 'sine.inOut',
          onStart: function () {
            if (!pinCollapsedForHandoff) unpinHeroForHandoff();
            handoffFromY = window.scrollY || window.pageYOffset || 0;
            handoffToY = getNextSectionScrollY();
            if (!isFinite(handoffFromY)) handoffFromY = 0;
            if (!isFinite(handoffToY)) handoffToY = handoffFromY;
            captureStickyHandoff();
            prepareHandoffShift();
            applyHandoffShift(0);
          },
          onUpdate: function () {
            applyHandoffShift((handoffToY - handoffFromY) * scrollProxy.t);
          },
          onComplete: function () {
            commitHandoffShift();
          }
        },
        handoffAt
      );
    }

    if (!autoplayTimeline.duration()) {
      finishCinematicAutoplay();
    }
  }

  function onSequenceUpdate(self) {
    if (jumpInProgress || restoringPin || autoPlaying) return;
    var p = clamp01(self.progress);
    applySequenceProgress(p);
    if (!introChecked) {
      if (maxFrameIndex() < 1) return;
      introChecked = true;
      if (!hasAutoPlayed && p > 0.02 && Math.round(p * maxFrameIndex()) >= triggerFrameIndex()) {
        hasAutoPlayed = true;
        fadeScrollHint();
        revealHero2Fx();
      }
    }
    if (hasAutoPlayed || prefersReducedMotion()) return;
    if (self.direction < 0) return;
    var frame = Math.round(p * maxFrameIndex());
    if (frame >= triggerFrameIndex()) {
      startCinematicAutoplay();
    }
  }

  function pinEndDistance() {
    var pin = pinEl;
    var screens = screensCount;
    if (!pin) return '+=100%';
    return '+=' + Math.round(pin.offsetHeight * screens);
  }

  function createSequenceTrigger(ScrollTrigger) {
    if (scrollTrigger) {
      scrollTrigger.kill();
      scrollTrigger = null;
    }

    scrollTrigger = ScrollTrigger.create({
      id: TRIGGER_ID,
      trigger: heroRoot,
      start: 'top top',
      end: pinEndDistance,
      pin: pinEl,
      pinType: 'fixed',
      pinSpacing: true,
      scrub: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onRefresh: function () {
        if (handoffRestorePending) snapToHandoffLanding();
      },
      onToggle: function (self) {
        if (!restoringPin && !handoffRestorePending) {
          setHeroPinnedState(self.isActive);
        }
        if (self.isActive) syncPinToVisualViewport();
        if (restoringPin) return;
        syncHeader();
      },
      onUpdate: onSequenceUpdate
    });

    if (scrollTrigger && !handoffRestorePending) {
      setHeroPinnedState(scrollTrigger.isActive);
    }
  }

  function destroy() {
    loadGen += 1;
    killAutoplayTimeline();
    autoPlaying = false;
    unlockUserInput();
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    lastTs = 0;
    introChecked = false;
    targetProgress = 0;
    displayedProgress = 0;
    drawnIndex = -1;
    drawnFadeKey = '';
    lastUiProgress = -1;
    frames = [];
    frameUrls = [];
    activeFrameSet = null;
    canvas = null;
    ctx = null;
    mediaEl = null;
    progressFill = null;
    progressKnobTravel = null;
    mediaCssW = 0;
    mediaCssH = 0;
    lastMediaBoxKey = '';
    frameTexts = [];
    lastTextFrame = -1;
    fadeMax = 0;
    fadeMaxMobile = 0;
    if (fadeTween) {
      if (fadeTween.scrollTrigger) fadeTween.scrollTrigger.kill();
      fadeTween.kill();
      fadeTween = null;
    }
    unbindPlayCue();
    jumpInProgress = false;
    setHeroPinnedState(false);
    heroRoot = null;
    pinEl = null;
    pinCollapsedForHandoff = false;
    restoringPin = false;
    clearHandoffShift();
    revealHero2Fx();

    resetScrollHint(document.querySelector('[data-leseni-hero2]'));

    if (scrollTrigger) {
      scrollTrigger.kill();
      scrollTrigger = null;
    } else if (window.ScrollTrigger) {
      var existing = window.ScrollTrigger.getById(TRIGGER_ID);
      if (existing) existing.kill();
    }
  }

  function onViewportChange() {
    var root = document.querySelector('[data-leseni-hero2]');
    if (!root || !canvas) return;

    var currentImg = drawnIndex >= 0 ? frameAt(drawnIndex) : null;
    if (currentImg) layoutMediaBox(currentImg);
    else resetMediaBox();
    resizeCanvas();
    if (drawnIndex >= 0) {
      var current = drawnIndex;
      drawnIndex = -1;
      drawnFadeKey = '';
      drawIndex(current, true);
    }

    var mobile = isMobileViewport();
    var nextSet = computeFrameSet();
    if (mobile === activeIsMobile && nextSet === activeFrameSet) return;

    if (autoPlaying) {
      hasAutoPlayed = true;
      killAutoplayTimeline();
      applySequenceProgress(1);
      displayedProgress = 1;
      if (pinCollapsedForHandoff) {
        restorePinAfterHandoff();
      } else {
        snapScrollToSequenceEnd();
      }
      autoPlaying = false;
      unlockUserInput();
    }

    collectFrameTexts(root);
    updateFrameTexts(progressToIndex());
    loadSequence(root);
  }

  function syncPinToVisualViewport() {
    if (!pinEl || !isMobileViewport()) return;
    var vis = window.visualViewport && window.visualViewport.height ? window.visualViewport.height : 0;
    if (!vis) return;
    if (vis > (pinEl.offsetHeight || 0) + 1) {
      pinEl.style.setProperty('min-height', vis + 'px', 'important');
      pinEl.style.setProperty('height', vis + 'px', 'important');
    }
  }

  function onVisualViewportChange() {
    syncPinToVisualViewport();
  }

  function bindViewportListeners() {
    if (resizeBound) return;
    resizeBound = true;
    window.addEventListener('resize', onViewportChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVisualViewportChange);
      window.visualViewport.addEventListener('scroll', onVisualViewportChange);
    }
  }

  function initHero2(gsap, ScrollTrigger) {
    var root = document.querySelector('[data-leseni-hero2]');
    if (!root) return;

    destroy();
    gsapRef = gsap;
    gsap.registerPlugin(ScrollTrigger);
    if (window.ScrollToPlugin) gsap.registerPlugin(window.ScrollToPlugin);
    ScrollTrigger.config({ ignoreMobileResize: true });

    heroRoot = root;
    pinEl = root.querySelector('[data-leseni-hero2-pin]');
    canvas = root.querySelector('[data-leseni-hero2-canvas]');
    ctx = canvas ? canvas.getContext('2d', { alpha: true }) : null;
    cacheUiNodes(root);
    rememberPinSize();
    screensCount = parseFloat(root.getAttribute('data-scroll-screens'));
    if (isNaN(screensCount) || screensCount < 1) screensCount = 4;
    autoplayStartFrame = parseInt(root.getAttribute('data-autoplay-start-frame'), 10);
    if (!isFinite(autoplayStartFrame) || autoplayStartFrame < 1) {
      autoplayStartFrame = TRIGGER_FRAME;
    }
    slowdownFrame = parseInt(root.getAttribute('data-autoplay-slowdown-frame'), 10);
    if (!isFinite(slowdownFrame) || slowdownFrame < 1) {
      slowdownFrame = SLOWDOWN_FRAME;
    }
    phase1DurationSec = clampDurationSec(
      root.getAttribute('data-autoplay-phase1-duration'),
      PHASE1_DURATION
    );
    phase2DurationSec = clampDurationSec(
      root.getAttribute('data-autoplay-phase2-duration'),
      PHASE2_DURATION
    );
    phase3DurationSec = clampDurationSec(
      root.getAttribute('data-autoplay-phase3-duration'),
      PHASE3_DURATION,
      10
    );
    overlapTimeSec = clampOverlapSec(
      root.getAttribute('data-autoplay-overlap-time'),
      OVERLAP_TIME
    );

    fadeMax = parseFloat(root.getAttribute('data-fade-height'));
    if (isNaN(fadeMax) || fadeMax < 0) fadeMax = 0;
    fadeMaxMobile = parseFloat(root.getAttribute('data-fade-height-mobile'));
    if (isNaN(fadeMaxMobile) || fadeMaxMobile < 0) fadeMaxMobile = fadeMax;

    lastUiProgress = -1;
    setProgressUI(root, 0);
    collectFrameTexts(root);

    if (!pinEl) {
      revealHero2Fx();
      return;
    }

    if (window.LeseniHero2Header && typeof window.LeseniHero2Header.attach === 'function') {
      window.LeseniHero2Header.attach();
    }

    bindViewportListeners();
    loadSequence(root);

    if (!canvas || prefersReducedMotion()) {
      hasAutoPlayed = true;
      revealHero2Fx();
      updateFrameTexts(0);
      if (!canvas) return;
    }

    startRaf();
    createSequenceTrigger(ScrollTrigger);
    bindPlayCue();

    if (hasAutoPlayed) {
      applySequenceProgress(clamp01(scrollTrigger.progress));
      displayedProgress = targetProgress;
    }
  }

  function bindBreakpointListener() {
    if (mqBound) return;
    mqBound = true;

    var mq = window.matchMedia(MOBILE_QUERY);
    var onChange = function () {
      onViewportChange();
    };

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(onChange);
    }
  }

  function boot() {
    bindBreakpointListener();
    waitForGsap(initHero2);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-leseni-hero2]')) {
      boot();
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-leseni-hero2]')) {
      if (window.LeseniHero2Header && typeof window.LeseniHero2Header.restore === 'function') {
        window.LeseniHero2Header.restore();
      }
      destroy();
    }
  });
})();
